import {
  OptionalString,
  PublishesTo,
  SchemaTypeHelper,
  StringEnum,
  SubscribesTo,
  createComponentAdmin,
} from '@backstage-components/base';
import {ComponentDefinition as RichTextComponentDefinition} from '@backstage-components/rich-text';
import {Static, Type} from '@sinclair/typebox';

export const reactName = 'MagicText';
const name = 'Magic Text';
const description = reactName;

export const textAnimateTriggerEnum = {
  pageLoad: 'On page load',
  inViewport: 'Scrolled into view',
  flowBroadcast: 'On Flow Broadcaster',
} as const;

const schema = Type.Object({
  magicTextRichText: Type.Object({
    ...RichTextComponentDefinition.schema.properties,
  }),
  textAnimateTrigger: StringEnum(
    [
      textAnimateTriggerEnum.pageLoad,
      textAnimateTriggerEnum.inViewport,
      textAnimateTriggerEnum.flowBroadcast,
    ],
    {
      title: 'Animation Trigger',
      description: 'Select when the magic text should animate in',
      default: 'On page load',
    }
  ),
  hoverImages: Type.Array(Type.String({title: 'Image'}), {
    title: 'Hover Images',
    description:
      'Images will appear on italicized words in the order they are added',
    maxItems: 30,
    minItems: 0,
  }),
  loaderColor: OptionalString({
    title: 'Loader Color',
    description:
      'The color of the loader shown while the hover images are loading. If a color is not specified the loader will not appear',
  }),
});

const uiSchema = {
  magicTextRichText: {
    'ui:title': 'Magic Text Content',
    'ui:description':
      'Within the text editor, italicize the words you would like to have the hover image reveal effect on',
    ...RichTextComponentDefinition.uiSchema,
  },
  textAnimateTrigger: {
    'ui:widget': 'select',
    'ui:options': {
      onPageLoad: textAnimateTriggerEnum.pageLoad,
      inViewport: textAnimateTriggerEnum.inViewport,
    },
  },
  loaderColor: {'ui:widget': 'color'},
  hoverImages: {
    items: {
      'ui:widget': 'AssetSelectWidget',
      'ui:options': {
        buttonTitle: 'Select Asset',
      },
    },
  },
};

const defaultFieldData: Static<typeof schema> = {
  magicTextRichText: RichTextComponentDefinition.defaultFieldData,
  textAnimateTrigger: textAnimateTriggerEnum.pageLoad,
  hoverImages: [],
};

const instructions = Type.Union([
  SubscribesTo({
    topic: `${reactName}:animate-text`,
    description:
      'Trigger the magic text to animate in. Note that the "Animation Trigger" property must be set to "On Flow Broadcaster" for this to work',
  }),
  PublishesTo({
    topic: `${reactName}:on-textAnimationComplete`,
    description: 'The magic text animation has completed',
  }),
]);

export const ComponentDefinition = createComponentAdmin({
  id: '726c4c72-f618-4113-bb87-cdfa92cffd7e',
  reactName,
  name,
  slug: reactName,
  description,
  version: 1,
  defaultFieldData,
  slotConfiguration: {},
  schema,
  uiSchema,
  instructions,
  category: 'custom',
})
  .withStyles()
  .build();

export type SchemaType = SchemaTypeHelper<typeof ComponentDefinition>;
